import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

/**
 * Convertir le texte arabe en image
 */
const textToImage = async (text, fontSize = 11) => {
    return new Promise((resolve) => {
        const div = document.createElement('div');
        div.style.position = 'absolute';
        div.style.left = '-9999px';
        div.style.top = '-9999px';
        div.style.backgroundColor = 'white';
        div.style.padding = '4px 8px';
        div.style.fontFamily = 'Arial, "Traditional Arabic", Tahoma, sans-serif';
        div.style.fontSize = `${fontSize}px`;
        div.style.direction = 'rtl';
        div.style.textAlign = 'right';
        div.style.maxWidth = '650px';
        div.style.width = 'max-content';
        div.style.whiteSpace = 'normal';
        div.style.wordBreak = 'break-word';
        div.style.lineHeight = '1.5';
        div.innerHTML = text;
        
        document.body.appendChild(div);
        
        html2canvas(div, {
            scale: 3, // Higher scale for better quality
            backgroundColor: '#ffffff',
            logging: false
        }).then(canvas => {
            document.body.removeChild(div);
            resolve({
                dataUrl: canvas.toDataURL('image/png'),
                width: canvas.width,
                height: canvas.height
            });
        }).catch(() => {
            document.body.removeChild(div);
            resolve(null);
        });
    });
};

/**
 * Génère une facture PDF pour une commande
 */
export const generateInvoicePDF = async (order, items, status = "completed") => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // ========== EN-TÊTE ==========
    doc.setFontSize(22);
    doc.setTextColor(0, 0, 51);
    doc.text("JAMAS LENS", 20, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text("Solutions de Climatisation Haut de Gamme", 20, 26);
    doc.text("www.jamaslens.ma", 20, 31);
    
    // Titre facture
    doc.setFontSize(18);
    doc.setTextColor(0);
    doc.text("FACTURE", pageWidth - 70, 20);
    
    // Infos commande
    doc.setFontSize(10);
    doc.text(`N° Commande: #${order.id}`, pageWidth - 70, 30);
    doc.text(`Date: ${order.date || new Date().toLocaleDateString()}`, pageWidth - 70, 35);
    doc.text(`Méthode: ${order.method?.toUpperCase() || "VIREMENT"}`, pageWidth - 70, 40);
    doc.text(`Statut: ${order.statut || "En cours"}`, pageWidth - 70, 45);
    
    // Ligne séparatrice
    doc.setDrawColor(200);
    const lineY = 50;
    doc.line(20, lineY, pageWidth - 20, lineY);
    
    // ========== INFORMATIONS CLIENT ==========
    const startY = 60;
    let currentY = startY;
    
    // Client
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Client:", 20, currentY);
    currentY += 6;
    
    // Nom complet
    doc.setFont("helvetica", "normal");
    const fullName = order.username_Client || order.nom_client || order.nom || "Client";
    doc.text(`Nom complet: ${fullName}`, 20, currentY);
    currentY += 6;
    
    // Adresse - Vérifier si contient de l'arabe
    const adresseTexte = order.adresse || "";
    const villeTexte = order.ville || "";
    const adresseComplete = adresseTexte + (villeTexte ? ` , ${villeTexte}` : "");
    
    // Détecter si c'est de l'arabe
    const hasArabic = /[\u0600-\u06FF]/.test(adresseComplete);
    
    if (hasArabic && adresseComplete) {
        // Pour l'arabe, utiliser html2canvas sur la même ligne
        const label = "Adresse: ";
        doc.text(label, 20, currentY);
        const labelWidth = doc.getTextWidth(label);
        
        const addressImg = await textToImage(adresseComplete, 14); // slightly larger font for generation
        if (addressImg && addressImg.dataUrl) {
            // Un font-size 14px avec line-height 1.5 sur canvas (scale 3) a une hauteur de ligne d'environ 63px.
            // On veut que cette ligne fasse environ 4.5mm dans le PDF.
            const mmPerPx = 4.5 / 63;
            const pdfWidth = addressImg.width * mmPerPx;
            const pdfHeight = addressImg.height * mmPerPx;
            
            // Draw image next to the label, adjusted slightly up to align with text baseline
            doc.addImage(addressImg.dataUrl, 'PNG', 20 + labelWidth + 1, currentY - 3.5, pdfWidth, pdfHeight);
            currentY += Math.max(6, pdfHeight + 2);
        } else {
            // Fallback
            doc.setFontSize(9);
            const textLines = doc.splitTextToSize(`Adresse: ${adresseComplete}`, 170);
            doc.text(textLines, 20, currentY);
            currentY += (textLines.length * 5) + 1;
            doc.setFontSize(10);
        }
    } else if (adresseComplete) {
        // Pour le français/latin - affichage normal
        const textLines = doc.splitTextToSize(`Adresse: ${adresseComplete}`, 170);
        doc.text(textLines, 20, currentY);
        currentY += (textLines.length * 5) + 1;
    }
    
    // Téléphone
    if (order.telephone) {
        doc.text(`Téléphone: ${order.telephone}`, 20, currentY);
        currentY += 6;
    }
    
    // ========== TABLEAU DES PRODUITS ==========
    const tableRows = items.map((item, index) => [
        index + 1,
        item.nomClimatisseur || item.nom || "Produit",
        `${item.taille || "Standard"} ${item.color ? `/ ${item.color}` : ""}`,
        item.quantite || 1,
        `${(item.prix || 0).toLocaleString()} DH`,
        `${((item.prix || 0) * (item.quantite || 1)).toLocaleString()} DH`
    ]);
    
    autoTable(doc, {
        startY: currentY + 5,
        head: [['#', 'Produit', 'Détails', 'Qté', 'Prix Unitaire', 'Total']],
        body: tableRows,
        theme: 'striped',
        headStyles: { 
            fillColor: [0, 0, 51], 
            textColor: [255, 255, 255] 
        },
        styles: { fontSize: 9, cellPadding: 4 },
        columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 60 },
            4: { halign: 'right' },
            5: { halign: 'right', fontStyle: 'bold' }
        },
    });
    
    // ========== TOTAL ==========
    const finalY = (doc.lastAutoTable?.finalY || currentY + 50) + 10;
    
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    
    doc.setTextColor(0);
    doc.text(`MONTANT TOTAL: ${(order.total || 0).toLocaleString()} DH`, pageWidth - 20, finalY + 10, { align: "right" });
    
    // Scan (QR code) a été supprimé à la demande
    
    // ========== PIED DE PAGE ==========
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.setFont("helvetica", "normal");
    
    doc.text("Merci pour votre confiance. En cas de question, contactez notre support", pageWidth / 2, pageHeight - 20, { align: "center" });
    doc.text("support@jamaslens.ma | 05 22 123 456", pageWidth / 2, pageHeight - 15, { align: "center" });
    
    // ========== TÉLÉCHARGEMENT ==========
    const fileName = `Facture_JamasLens_${order.id}.pdf`;
    
    doc.save(fileName);
    
    return {
        success: true,
        fileName: fileName,
        orderId: order.id,
        status: status
    };
};

// ========== FONCTION PRINCIPALE ==========
export const downloadInvoice = async (order, items, type) => {
    const status = type === "dewez" ? "completed" : "pending";
    
    try {
        const result = await generateInvoicePDF(order, items, status);
        console.log(`✅ Facture téléchargée: ${result.fileName}`);
        return result;
    } catch (error) {
        console.error("❌ Erreur lors du téléchargement:", error);
        throw error;
    }
};

export default generateInvoicePDF;